import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { fmtMoney, fmtDate, todayISO } from "@/lib/format";
import { printBalanceSheet } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";

const zeroSettings = {
  cash_in_hand: 0,
  bank_balance: 0,
  upi_balance: 0,
  other_assets: 0,
  business_borrowings: 0,
  other_payables: 0,
  other_liabilities: 0,
  owner_capital: 0,
  drawings: 0,
};

const moneyFields = Object.keys(zeroSettings);

export default function BalanceSheet() {
  const { settings, business, currency, dateFormat } = useSettings();

  const [asOn, setAsOn] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [opening, setOpening] = useState(zeroSettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get("/balance-sheet/settings");
      setOpening({ ...zeroSettings, ...(res.data || {}) });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, []);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/balance-sheet", {
        params: { as_on: asOn },
      });
      setReport(res.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [asOn]);

  useEffect(() => {
    loadSettings();
    generate();
  }, [loadSettings, generate]);

  const updateOpening = (field, value) => {
    setOpening((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = {};
      moneyFields.forEach((field) => {
        payload[field] = Number(opening[field]) || 0;
      });

      await api.put("/balance-sheet/settings", payload);
      toast.success("Balance Sheet opening balances saved.");
      await generate();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key) => (
    <div>
      <Label>{label}</Label>
      <Input
        type="number"
        min="0"
        step="0.01"
        value={opening[key]}
        onChange={(e) => updateOpening(key, e.target.value)}
      />
    </div>
  );

  const amount = (value) => fmtMoney(Number(value) || 0, currency);

  return (
    <div className="space-y-6" data-testid="balance-sheet-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">
            Balance Sheet
          </h1>
          <p className="text-slate-500 mt-1">
            Financial position of your lending business
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() =>
            report && printBalanceSheet(report, business, settings)
          }
          disabled={!report || loading}
          data-testid="print-balance-sheet-button"
        >
          <Printer className="h-4 w-4 mr-2" />
          Print / Save PDF
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Balance Sheet As On</Label>
            <Input
              type="date"
              value={asOn}
              onChange={(e) => setAsOn(e.target.value)}
            />
          </div>

          <Button
            className="bg-navy hover:bg-navy-hover"
            onClick={generate}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Generating..." : "Generate"}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-heading text-lg font-bold text-navy">
              Opening / Manual Balances
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Enter balances that are not automatically calculated from loans,
              payments, or expenses.
            </p>
          </div>

          <Button
            variant="outline"
            onClick={saveSettings}
            disabled={saving}
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Balances"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {field("Cash in Hand", "cash_in_hand")}
          {field("Bank Balance", "bank_balance")}
          {field("UPI Balance", "upi_balance")}
          {field("Other Assets", "other_assets")}
          {field("Business Borrowings", "business_borrowings")}
          {field("Other Payables", "other_payables")}
          {field("Other Liabilities", "other_liabilities")}
          {field("Owner Capital", "owner_capital")}
          {field("Drawings", "drawings")}
        </div>
      </div>

      {loading && (
        <p className="text-slate-400">Generating Balance Sheet...</p>
      )}

      {report && !loading && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200 text-center">
            <h2 className="font-heading text-2xl font-bold text-navy">
              Balance Sheet
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              As on{" "}
              <b className="text-navy">
                {fmtDate(report.as_on, dateFormat)}
              </b>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
            <div className="p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-navy border-l-4 border-maroon pl-3 mb-4">
                Assets
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Cash in Hand</span>
                  <span className="font-mono">{amount(report.assets.cash_in_hand)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Bank Balance</span>
                  <span className="font-mono">{amount(report.assets.bank_balance)}</span>
                </div>
                <div className="flex justify-between">
                  <span>UPI Balance</span>
                  <span className="font-mono">{amount(report.assets.upi_balance)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Principal Outstanding</span>
                  <span className="font-mono">{amount(report.assets.principal_outstanding)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Interest Receivable</span>
                  <span className="font-mono">{amount(report.assets.interest_receivable)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Other Assets</span>
                  <span className="font-mono">{amount(report.assets.other_assets)}</span>
                </div>

                <div className="flex justify-between border-t-2 border-slate-300 pt-3 font-bold text-base">
                  <span>Total Assets</span>
                  <span className="font-mono text-navy">
                    {amount(report.assets.total_assets)}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-navy border-l-4 border-maroon pl-3 mb-4">
                Liabilities
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Business Borrowings</span>
                  <span className="font-mono">{amount(report.liabilities.business_borrowings)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Other Payables</span>
                  <span className="font-mono">{amount(report.liabilities.other_payables)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Other Liabilities</span>
                  <span className="font-mono">{amount(report.liabilities.other_liabilities)}</span>
                </div>

                <div className="flex justify-between border-t-2 border-slate-300 pt-3 font-bold text-base">
                  <span>Total Liabilities</span>
                  <span className="font-mono text-navy">
                    {amount(report.liabilities.total_liabilities)}
                  </span>
                </div>
              </div>

              <h3 className="text-sm font-bold uppercase tracking-wider text-navy border-l-4 border-maroon pl-3 mt-8 mb-4">
                Owner's Equity
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Owner Capital</span>
                  <span className="font-mono">{amount(report.equity.owner_capital)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Accumulated Profit</span>
                  <span className="font-mono">{amount(report.equity.accumulated_profit)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Less: Drawings</span>
                  <span className="font-mono">{amount(report.equity.drawings)}</span>
                </div>

                <div className="flex justify-between border-t-2 border-slate-300 pt-3 font-bold text-base">
                  <span>Total Equity</span>
                  <span className="font-mono text-navy">
                    {amount(report.equity.total_equity)}
                  </span>
                </div>
              </div>

              {/* FINLEND LOAN & COLLECTION SUMMARY */}
            {report.dashboard_reference && (
              <div className="m-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="mb-4">
                  <h3 className="font-heading text-lg font-bold text-navy">
                    FinLend Loan & Collection Summary
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Historical lending, collections, profit and outstanding position as on the selected date.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                  {[
                    ["Total Money Lent", "total_money_lent"],
                    ["Repayments Received", "repayments_received"],
                    ["Interest / Loan Profit", "interest_profit"],
                    ["Principal Collected", "principal_collected"],
                    ["Interest Collected", "interest_collected"],
                    ["Cash Collected", "cash_collected"],
                    ["UPI Collected", "upi_collected"],
                    ["Principal Outstanding", "principal_outstanding"],
                    ["Interest Outstanding", "interest_outstanding"],
                    ["Total Outstanding", "outstanding"],
                    ["Total Expenses", "total_expenses"],
                    ["Net Profit After Expenses", "net_profit"],
                  ].map(([label, key]) => (
                    <div
                      key={key}
                      className="flex flex-col bg-white rounded-lg border p-3 min-w-0"
                    >
                      <span className="text-slate-600 leading-5">{label}</span>
                      <span className="font-mono font-bold text-right text-base break-words whitespace-normal mt-1">
                        {amount(report.dashboard_reference[key])}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FINLEND RECONCILIATION */}
            {report.reconciliation && (
              <div className="mx-6 mb-6 rounded-xl border border-slate-200 p-5">
                <div className="mb-4">
                  <h3 className="font-heading text-lg font-bold text-navy">
                    FinLend Reconciliation
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Confirms that lending, profit, collections and outstanding balances reconcile.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-lg border bg-slate-50 p-4">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Gross Reconciliation
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-sm min-w-0">
                      <span>Money Lent + Interest Profit</span>
                      <span className="font-mono font-bold">
                        {amount(report.reconciliation.gross_left)}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-sm mt-2 min-w-0">
                      <span>Outstanding + Repayments Received</span>
                      <span className="font-mono font-bold">
                        {amount(report.reconciliation.gross_right)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t mt-3 pt-3 font-bold">
                      <span>Difference</span>
                      <span className="font-mono">
                        {amount(report.reconciliation.gross_difference)}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg border bg-slate-50 p-4">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Net Reconciliation
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-sm min-w-0">
                      <span>Money Lent + Net Profit</span>
                      <span className="font-mono font-bold">
                        {amount(report.reconciliation.net_left)}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-sm mt-2 min-w-0">
                      <span>Outstanding + Collections - Expenses</span>
                      <span className="font-mono font-bold">
                        {amount(report.reconciliation.net_right)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t mt-3 pt-3 font-bold">
                      <span>Difference</span>
                      <span className="font-mono">
                        {amount(report.reconciliation.net_difference)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between border-t-4 border-navy mt-6 pt-4 font-bold text-lg">
                <span>Liabilities + Equity</span>
                <span className="font-mono">
                  {amount(report.total_liabilities_and_equity)}
                </span>
              </div>
            </div>
          </div>

          <div
            className={`m-6 rounded-xl border p-5 ${
              report.is_balanced
                ? "bg-emerald-50 border-emerald-200"
                : "bg-red-50 border-red-200"
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Balance Check
                </div>
                <div
                  className={`mt-1 text-xl font-bold ${
                    report.is_balanced ? "text-emerald-700" : "text-red-600"
                  }`}
                >
                  {report.is_balanced
                    ? "BALANCE SHEET BALANCED"
                    : "BALANCE SHEET NOT BALANCED"}
                </div>
              </div>

              <div className="text-right">
                <div className="text-xs text-slate-500">
                  Difference
                </div>
                <div className="font-mono font-bold">
                  {amount(report.balance_difference)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
